import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type MechanicListItem = {
  id: string;
  userId: string;
  displayName: string | null;
  idCardNo: string | null;
  status: string;
  createdAt: string;
  image: string | null;
};

export type MechanicListResult = {
  data: MechanicListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type MechanicStats = {
  pendingVerification: number;
  active: number;
  suspended: number;
};

export type MechanicListQuery = {
  limit?: number;
  offset?: number;
  status?: "all" | "pending_verification" | "active" | "suspended";
  q?: string;
  sortBy?: "createdAt" | "displayName";
  sortOrder?: "asc" | "desc";
};

export type MechanicServiceRow = {
  id: string;
  serviceKey: string;
  title: string;
  basePrice: number | null;
  currency: string;
  isActive: boolean;
};

export type MechanicDetail = {
  id: string;
  userId: string;
  status: string;
  idCardNo: string | null;
  idCardImageKey: string | null;
  verifiedAt: string | null;
  createdAt: string;
  displayName: string | null;
  bio: string | null;
  serviceAreaCity: string | null;
  serviceRadiusKm: number | null;
  profileCreatedAt: string | null;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  userImage: string | null;
  userBanned: boolean | null;
  services: MechanicServiceRow[];
};

export const mechanicsApi = {
  async list(query: MechanicListQuery = {}): Promise<MechanicListResult> {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.status && query.status !== "all")
      params.set("status", query.status);
    if (query.q?.trim()) params.set("q", query.q.trim());
    if (query.sortBy) params.set("sortBy", query.sortBy);
    if (query.sortOrder) params.set("sortOrder", query.sortOrder);
    return fetcher.get<MechanicListResult>(
      `${config.apiUrl}/mechanics?${params.toString()}`,
    );
  },

  async stats(): Promise<MechanicStats> {
    return fetcher.get<MechanicStats>(`${config.apiUrl}/mechanics/stats`);
  },

  async getById(id: string): Promise<MechanicDetail> {
    return fetcher.get<MechanicDetail>(`${config.apiUrl}/mechanics/${id}`);
  },

  async verify(id: string): Promise<{ ok: true }> {
    return fetcher.post<{ ok: true }>(
      `${config.apiUrl}/mechanics/${id}/verify`,
    );
  },

  async reject(
    id: string,
    body: { reason?: string } = {},
  ): Promise<{ ok: true }> {
    return fetcher.post<{ ok: true }>(
      `${config.apiUrl}/mechanics/${id}/reject`,
      body,
    );
  },

  async suspend(
    id: string,
    body: { reason?: string } = {},
  ): Promise<{ ok: true }> {
    return fetcher.post<{ ok: true }>(
      `${config.apiUrl}/mechanics/${id}/suspend`,
      body,
    );
  },

  async unsuspend(
    id: string,
    body: { reason?: string } = {},
  ): Promise<{ ok: true }> {
    return fetcher.post<{ ok: true }>(
      `${config.apiUrl}/mechanics/${id}/unsuspend`,
      body,
    );
  },

  async updateIdCardImage(id: string, key: string): Promise<{ ok: true }> {
    return fetcher.put<{ ok: true }>(
      `${config.apiUrl}/mechanics/${id}/id-card-image`,
      { key },
    );
  },

  async getPerformance(
    id: string,
    period: "day" | "week" | "month" = "week",
  ): Promise<MechanicPerformanceResult> {
    return fetcher.get<MechanicPerformanceResult>(
      `${config.apiUrl}/mechanics/${id}/performance?period=${period}`,
    );
  },
};

export type Period = "day" | "week" | "month";

export type MechanicPerformanceResult = {
  jobsCompleted: number;
  jobsCompletedChangePercent: number;
  quotesSent: number;
  conversionRatePercent: number;
  avgRating: number;
  reviewCount: number;
  estEarnings: number;
  estEarningsChange: number;
  jobTrends: Array<{ day: string; count: number }>;
  earningsComparison: Array<{
    week: string;
    target: number;
    actual: number;
  }>;
  recentReviews: Array<{
    id: string;
    rating: number;
    content: string | null;
    createdAt: string;
    reviewerName: string | null;
  }>;
};
